// ============================================================
// backup — export / import everything LinksLab keeps in the browser
//
// All app state lives in localStorage under keys prefixed 'sc_'
// (sc_recipes, sc_ingredient_db, sc_ingredient_db_version, sc_methods).
// Nothing is stored on a server, so a downloaded backup file is the
// only durable copy of a user's formulas.
//
// We snapshot every 'sc_' key rather than a fixed list, so keys added
// later are captured without touching this file.
// ============================================================

export const FORMAT      = 'linkslab-backup';
export const VERSION     = 1;
const KEY_PREFIX         = 'sc_';
const PRE_RESTORE_KEY    = 'linkslab_pre_restore_snapshot';

// ── Read every sc_ key into a plain object ──────────────────
function snapshot() {
  const out = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(KEY_PREFIX)) out[key] = localStorage.getItem(key);
  }
  return out;
}

function parseRecipes(raw) {
  try {
    const list = JSON.parse(raw || '[]');
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

// ── Build the backup object ─────────────────────────────────
export function buildBackup() {
  const data = snapshot();
  return {
    format:     FORMAT,
    version:    VERSION,
    app:        'LinksLab',
    exportedAt: new Date().toISOString(),
    recipeCount: parseRecipes(data.sc_recipes).length,
    data,
  };
}

export function backupFilename(date = new Date()) {
  const pad = n => String(n).padStart(2, '0');
  const stamp = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `linkslab-backup-${stamp}.json`;
}

// ── Trigger a browser download of the backup ────────────────
export function downloadBackup() {
  const backup = buildBackup();
  const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = backupFilename();
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke on the next tick so the download has taken the URL
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return backup;
}

// ── Validate and parse a backup file's text ─────────────────
// Throws with a readable message rather than returning junk.
export function parseBackup(text) {
  let parsed;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    throw new Error("That file isn't valid JSON — it may be damaged or not a backup file.");
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error("That doesn't look like a LinksLab backup file.");
  }
  // A shared recipe is not a backup: restoring one would replace the cook's
  // whole library with a single recipe. Say which door to use.
  if (parsed.format === 'mesarobles-recipe-package') {
    throw new Error('This is a shared recipe — use "Open shared recipe" to add it to your recipes.');
  }
  if (parsed.format !== FORMAT) {
    throw new Error("That doesn't look like a LinksLab backup file.");
  }
  if (!parsed.data || typeof parsed.data !== 'object') {
    throw new Error('That backup file has no data in it.');
  }
  return parsed;
}

// ── Describe what a backup contains, before applying it ─────
export function describeBackup(backup) {
  const incoming = parseRecipes(backup.data.sc_recipes);
  const current  = parseRecipes(localStorage.getItem('sc_recipes'));
  const currentIds = new Set(current.map(r => r.id));
  const newOnes  = incoming.filter(r => !currentIds.has(r.id));

  return {
    exportedAt:      backup.exportedAt || null,
    incomingRecipes: incoming.length,
    currentRecipes:  current.length,
    wouldAdd:        newOnes.length,
    alreadyPresent:  incoming.length - newOnes.length,
    hasIngredientDb: Boolean(backup.data.sc_ingredient_db),
  };
}

// ── Apply a backup ──────────────────────────────────────────
// mode 'merge'   — add recipes not already present; leave existing untouched
// mode 'replace' — wipe all sc_ keys, restore the file exactly
//
// Either way we stash the pre-restore state first so it can be undone.
export function applyBackup(backup, mode = 'merge') {
  const before = snapshot();
  try {
    localStorage.setItem(PRE_RESTORE_KEY, JSON.stringify({
      savedAt: new Date().toISOString(),
      data: before,
    }));
  } catch (e) {
    // Snapshot is a convenience, not a requirement — carry on if quota-blocked
  }

  if (mode === 'replace') {
    Object.keys(before).forEach(key => localStorage.removeItem(key));
    Object.entries(backup.data).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
    return {
      mode,
      added: parseRecipes(backup.data.sc_recipes).length,
      skipped: 0,
      costsRestored: countCosts(safeParse(backup.data.sc_ingredient_db, {})),
      customAdded: 0,
    };
  }

  // ── merge ──
  const incoming   = parseRecipes(backup.data.sc_recipes);
  const current    = parseRecipes(localStorage.getItem('sc_recipes'));
  const currentIds = new Set(current.map(r => r.id));
  const toAdd      = incoming.filter(r => !currentIds.has(r.id));

  localStorage.setItem('sc_recipes', JSON.stringify([...current, ...toAdd]));

  const dbResult = mergeIngredientDb(backup);

  // Any other sc_ key we don't already have (e.g. sc_methods)
  Object.entries(backup.data).forEach(([key, value]) => {
    if (key === 'sc_recipes') return;
    if (key.startsWith('sc_ingredient_db')) return;
    if (localStorage.getItem(key) === null) localStorage.setItem(key, value);
  });

  return {
    mode,
    added:   toAdd.length,
    skipped: incoming.length - toAdd.length,
    costsRestored: dbResult.costsRestored,
    customAdded:   dbResult.customAdded,
  };
}

// ── Merge the ingredient database ───────────────────────────
// useIngredientDB writes the default database to localStorage the moment the
// app mounts, so by the time anyone opens the backup dialog there is always a
// database present. Skipping the restore when one exists therefore never
// restored anything — it silently dropped the user's costs.
//
// Defaults ship with costPerUnit 0, so a cost above zero is user-entered data.
// We copy those (with their unit — cost per gram is wrong without it) onto
// matching ingredients that have no cost of their own, and add any custom
// ingredients the current database lacks. Everything else is left alone so an
// updated default database keeps its improvements.
function mergeIngredientDb(backup) {
  const raw = backup.data.sc_ingredient_db;
  if (!raw) return { costsRestored: 0, customAdded: 0 };

  const stamp = backup.data.sc_ingredient_db_version;
  const setStamp = () => {
    // The stamp must travel with the data or useIngredientDB decides the
    // database is stale, discards it, and rebuilds the defaults on next load.
    if (stamp) localStorage.setItem('sc_ingredient_db_version', stamp);
  };

  const currentRaw = localStorage.getItem('sc_ingredient_db');
  if (!currentRaw) {
    localStorage.setItem('sc_ingredient_db', raw);
    setStamp();
    const restored = countCosts(safeParse(raw, {}));
    return { costsRestored: restored, customAdded: 0 };
  }

  const incoming = safeParse(raw, null);
  const current  = safeParse(currentRaw, null);
  if (!incoming || !current) return { costsRestored: 0, customAdded: 0 };

  let costsRestored = 0;
  let customAdded   = 0;

  Object.entries(incoming).forEach(([section, list]) => {
    if (!Array.isArray(list)) return;
    const currentList = Array.isArray(current[section]) ? current[section] : [];
    // Default ids are derived from the name, so id and name both identify a row
    const byId   = new Map(currentList.map(i => [i.id, i]));
    const byName = new Map(currentList.map(i => [i.name, i]));

    list.forEach(saved => {
      const match = byId.get(saved.id) || byName.get(saved.name);

      if (!match) {
        currentList.push(saved);
        customAdded++;
        return;
      }

      const savedCost   = Number(saved.costPerUnit) || 0;
      const currentCost = Number(match.costPerUnit) || 0;
      if (savedCost > 0 && currentCost === 0) {
        match.costPerUnit = saved.costPerUnit;
        if (saved.costUnit) match.costUnit = saved.costUnit;
        costsRestored++;
      }
    });

    current[section] = currentList;
  });

  localStorage.setItem('sc_ingredient_db', JSON.stringify(current));
  setStamp();
  return { costsRestored, customAdded };
}

function safeParse(text, fallback) {
  try {
    return JSON.parse(text);
  } catch (e) {
    return fallback;
  }
}

function countCosts(db) {
  return Object.values(db)
    .filter(Array.isArray)
    .reduce((n, list) => n + list.filter(i => (Number(i.costPerUnit) || 0) > 0).length, 0);
}

// ── Undo the most recent restore ────────────────────────────
export function hasPreRestoreSnapshot() {
  return Boolean(localStorage.getItem(PRE_RESTORE_KEY));
}

export function undoRestore() {
  const raw = localStorage.getItem(PRE_RESTORE_KEY);
  if (!raw) throw new Error('There is nothing to undo.');
  let snap;
  try {
    snap = JSON.parse(raw);
  } catch (e) {
    throw new Error('The undo snapshot is damaged and cannot be used.');
  }
  const data = (snap && snap.data) || {};
  Object.keys(snapshot()).forEach(key => localStorage.removeItem(key));
  Object.entries(data).forEach(([key, value]) => localStorage.setItem(key, value));
  localStorage.removeItem(PRE_RESTORE_KEY);
  return { restoredRecipes: parseRecipes(data.sc_recipes).length };
}
